import { getStoredToken } from './auth';
import { authenticatedFetch } from './api-client';
import { MultiplayerSession, MultiplayerTurnMode, MultiplayerEvent, GameMode, Difficulty, Genre, StoryOption } from '../types';

const API_BASE = '';

/**
 * Create a new multiplayer session
 */
export async function createMultiplayerSession(
  turnMode: MultiplayerTurnMode,
  gameMode: GameMode = 'adventure',
  difficulty: Difficulty = 2,
  genre: Genre = 'random',
  votingEnabled: boolean = true,
  autoSendLimit: number = 0,
  maxPlayers: number = 10
): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=create`, {
    method: 'POST',
    body: JSON.stringify({ turnMode, gameMode, difficulty, genre, votingEnabled, autoSendLimit, maxPlayers }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create session');
  }

  return response.json();
}

/**
 * Join an existing multiplayer session
 */
export async function joinMultiplayerSession(sessionId: string): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=join`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to join session');
  }

  return response.json();
}

/**
 * Leave the current multiplayer session
 */
export async function leaveMultiplayerSession(sessionId: string): Promise<{ success: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=leave`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to leave session');
  }

  return response.json();
}

/**
 * Start a multiplayer game (host only)
 */
export async function startMultiplayerGame(sessionId: string): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=start`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to start game');
  }

  return response.json();
}

/**
 * Get current session state
 */
export async function getMultiplayerSession(sessionId: string): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=get&sessionId=${encodeURIComponent(sessionId)}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get session');
  }

  return response.json();
}

/**
 * Update game state (input, suggestions, messages)
 */
export async function updateMultiplayerGameState(
  sessionId: string,
  update: {
    currentInput?: string;
    suggestions?: string[];
    newMessage?: { id: string; text: string; sender: string; timestamp: number };
    winCondition?: { objective: string; hint: string };
    adventurePlan?: unknown;
  }
): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=update`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, ...update }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update game state');
  }

  return response.json();
}

/**
 * Select a prediction word (per_prediction mode)
 */
export async function selectMultiplayerPrediction(
  sessionId: string,
  word: string
): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=select_prediction`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, word }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to select prediction');
  }

  return response.json();
}

/**
 * End the multiplayer game
 */
export async function endMultiplayerGame(
  sessionId: string,
  outcome: string,
  reason: string
): Promise<{ success: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=end`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, outcome, reason }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to end game');
  }

  return response.json();
}

/**
 * Subscribe to multiplayer session events using SSE
 */
export function subscribeToMultiplayerEvents(
  sessionId: string,
  onEvent: (event: MultiplayerEvent) => void,
  onError?: (error: Error) => void
): () => void {
  let aborted = false;
  let eventSource: EventSource | null = null;
  
  // Check authentication first
  const token = getStoredToken();
  if (!token) {
    // Schedule error callback for next tick
    if (onError) {
      setTimeout(() => onError(new Error('Not authenticated')), 0);
    }
    return () => {
      aborted = true;
    };
  }
  
  try {
    // Create EventSource with the token in the URL (since EventSource doesn't support custom headers)
    const url = `${API_BASE}/api/multiplayer?action=events&sessionId=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(token)}`;
    eventSource = new EventSource(url);
    
    eventSource.onmessage = (e) => {
      if (aborted) return;
      
      try {
        const event = JSON.parse(e.data) as MultiplayerEvent;
        onEvent(event);
      } catch (err) {
        console.error('Failed to parse event:', err);
      }
    };
    
    eventSource.onerror = (err) => {
      if (aborted) return;
      
      console.error('SSE error:', err);
      if (onError) {
        onError(new Error('Connection lost'));
      }
      
      // EventSource will automatically reconnect, but if it keeps failing we should close it
      if (eventSource && eventSource.readyState === EventSource.CLOSED) {
        eventSource.close();
        eventSource = null;
      }
    };
  } catch (err) {
    if (onError) {
      onError(err instanceof Error ? err : new Error('Failed to create event source'));
    }
  }
  
  // Return cleanup function
  return () => {
    aborted = true;
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  };
}

/**
 * Copy session code to clipboard
 */
export async function copySessionCode(sessionId: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(sessionId);
    return true;
  } catch (error) {
    console.error('Failed to copy session code:', error);
    return false;
  }
}

/**
 * Send an emoji reaction in multiplayer session
 */
export async function sendEmojiReaction(
  sessionId: string,
  emoji: string
): Promise<{ success: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=send_emoji`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, emoji }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send emoji reaction');
  }

  return response.json();
}

/**
 * Start voting phase for adventure selection
 */
export async function startVoting(
  sessionId: string,
  options: StoryOption[]
): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=start_voting`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, options }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to start voting');
  }

  return response.json();
}

/**
 * Cast a vote for an adventure option
 */
export async function castVote(
  sessionId: string,
  optionId: string
): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=cast_vote`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, optionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to cast vote');
  }

  return response.json();
}

/**
 * End voting and get the selected adventure
 */
export async function endVotingSession(
  sessionId: string
): Promise<{ success: boolean; session: MultiplayerSession; selectedOption: StoryOption }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=end_voting`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to end voting');
  }

  return response.json();
}

/**
 * Pass turn to next player (when auto-send timer expires with no input)
 */
export async function passTurnToNextPlayer(
  sessionId: string
): Promise<{ success: boolean; session: MultiplayerSession }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=pass_turn`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to pass turn');
  }

  return response.json();
}

/**
 * Publish a streaming chunk to all players in the session
 */
export async function publishStreamingChunk(
  sessionId: string,
  chunk: string
): Promise<{ success: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=publish_streaming_chunk`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, chunk }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to publish streaming chunk');
  }

  return response.json();
}

/**
 * Publish streaming complete event to all players
 */
export async function publishStreamingComplete(
  sessionId: string
): Promise<{ success: boolean }> {
  const response = await authenticatedFetch(`${API_BASE}/api/multiplayer?action=publish_streaming_complete`, {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to publish streaming complete');
  }

  return response.json();
}
